import styled, { css } from 'styled-components';

import { COLOR, mediaQueries, mixins, textStyles } from '#@/shared/utils/styles';

import Clickable, { type ClickableProps } from '../Clickable';

const HORIZONTAL_OFFSET_MOBILE = 14;
const VERTICAL_OFFSET_MOBILE = 14;

const HORIZONTAL_OFFSET_DESKTOP = 14;
const VERTICAL_OFFSET_DESKTOP = 9;

const selectedCss = css`
  background-color: ${COLOR.brand};
  color: #fff;
`;

const pressedCss = css`
  background-color: ${COLOR.brandLighterAlpha};
  color: ${COLOR.brand};
`;

const hoveredCss = css`
  background-color: ${COLOR.brandLightestAlpha};
  color: ${COLOR.brand};
`;

export interface DropdownActionWrapperProps extends ClickableProps {
  $selected?: boolean;
  $hovered?: boolean;
}

export const DropdownActionWrapper = styled(Clickable).attrs({
  'data-cy-dropdown-action': true,
})<DropdownActionWrapperProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  color: ${COLOR.textSecondary};
  white-space: nowrap;
  border-radius: 4px;
  margin: 1px 0;
  ${mixins.clickable()};

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  @media ${mediaQueries.mobile} {
    padding: ${VERTICAL_OFFSET_MOBILE}px ${HORIZONTAL_OFFSET_MOBILE}px;
    ${textStyles.labelLarge500};
  }

  @media ${mediaQueries.tablet} {
    padding: ${VERTICAL_OFFSET_DESKTOP}px ${HORIZONTAL_OFFSET_DESKTOP}px;
    ${textStyles.labelMedium500};
  }

  &:not(:disabled) {
    ${(props) =>
      props.$selected
        ? selectedCss
        : css`
            &:hover {
              ${hoveredCss};
            }
            ${props.$hovered && hoveredCss};

            &:active {
              ${pressedCss};
            }
          `}

    &.active, &.active:hover, &.active:active {
      ${selectedCss}
    }
  }

  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }

  &[hidden] {
    display: none;
  }
`;

export const DropdownActionSeparator = styled.div`
  height: 1px;
  background-color: ${COLOR.borderLight};

  @media ${mediaQueries.mobile} {
    margin: 8px ${HORIZONTAL_OFFSET_MOBILE}px;
  }

  @media ${mediaQueries.tablet} {
    margin: 8px ${HORIZONTAL_OFFSET_DESKTOP}px;
  }
`;

const IconOrControlWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`;

export const IconWrapper = styled(IconOrControlWrapper)`
  width: 18px;
  height: 18px;
`;

export const CheckboxWrapper = styled(IconOrControlWrapper)`
  label {
    /* This prevents the checkbox/radio button from capturing click events.
    Without this, clicking directly on the checkbox/radio button leads to
    two click events bubbling up that cancel each other out. */
    pointer-events: none;
  }
`;

export const RadioButtonWrapper = styled(IconOrControlWrapper)`
  width: 18px;
  height: 18px;
`;

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
`;

interface SubtitleBottomProps {
  $selected?: boolean;
}

export const SubtitleBottom = styled.div<SubtitleBottomProps>`
  ${mixins.truncateText()}

  ${({ $selected }) => css`
    ${textStyles.bodySmall450};
    color: ${$selected ? 'inherit' : COLOR.textTertiary};
    margin-top: 2px;
  `}
`;

interface SubtitleRightProps {
  $selected?: boolean;
}

export const SubtitleRight = styled.div<SubtitleRightProps>`
  ${textStyles.bodySmall450};
  color: ${(props) => (props.$selected ? 'inherit' : COLOR.textTertiary)};
`;

export const DropdownSectionHeading = styled.div`
  ${textStyles.sectionTitle700};
  color: ${COLOR.textTertiary};
  padding: 10px ${HORIZONTAL_OFFSET_DESKTOP}px;
  margin: 10px 0 4px;

  &:first-child {
    margin-top: 0;
  }
`;

export const IconRightWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
